import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Badge,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Snackbar,
  IconButton
} from '@mui/material';
import {
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Tune as TuneIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';

// Componentes propios
import TarjetaEmpresa from './TarjetaEmpresa';
import DialogAprobarNegocio from './DialogAprobarNegocio';
import DialogConfigurarEmpresa from './DialogConfigurarEmpresa';

// APIs
import empresasApi from '../../../api/empresasApi';

/**
 * TabPanel - Componente auxiliar para renderizar contenido de tabs
 */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * GestionEmpresasUnificada
 *
 * Componente principal que unifica la gestión de empresas/negocios
 * con sistema de tabs: Pendientes | Activas | Todas
 */
function GestionEmpresasUnificada() {
  const navigate = useNavigate();

  // Estados principales
  const [tabActual, setTabActual] = useState(0);
  const [loading, setLoading] = useState(true);
  const [empresas, setEmpresas] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [activas, setActivas] = useState([]);

  // Estados para dialogs
  const [dialogAprobarOpen, setDialogAprobarOpen] = useState(false);
  const [dialogConfigurarOpen, setDialogConfigurarOpen] = useState(false);
  const [negocioSeleccionado, setNegocioSeleccionado] = useState(null);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);

  // Estado para mensajes
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  /**
   * Cargar empresas al montar el componente
   */
  useEffect(() => {
    cargarEmpresas();
  }, []);

  /**
   * Cargar todas las empresas y filtrar por estado
   */
  const cargarEmpresas = async () => {
    setLoading(true);
    try {
      const response = await empresasApi.listar();
      const data = response.data || [];

      setEmpresas(data);

      // Filtrar pendientes (estado_proceso = 'pendiente' o null)
      const pendientesData = data.filter(emp =>
        !emp.estado_proceso || emp.estado_proceso === 'pendiente'
      );
      setPendientes(pendientesData);

      // Filtrar activas (estado_proceso = 'aprobado' o 'activo')
      const activasData = data.filter(emp =>
        emp.estado_proceso === 'aprobado' || emp.estado_proceso === 'activo'
      );
      setActivas(activasData);

    } catch (error) {
      console.error('Error cargando empresas:', error);
      mostrarMensaje('Error al cargar empresas', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cambiar tab
   */
  const handleChangeTab = (event, newValue) => {
    setTabActual(newValue);
  };

  /**
   * Abrir dialog de aprobación
   */
  const handleAbrirDialogAprobar = (negocio) => {
    setNegocioSeleccionado(negocio);
    setDialogAprobarOpen(true);
  };

  /**
   * Cerrar dialog de aprobación
   */
  const handleCerrarDialogAprobar = () => {
    setDialogAprobarOpen(false);
    setNegocioSeleccionado(null);
  };

  /**
   * Callback cuando se aprueba un negocio
   */
  const handleNegocioAprobado = async () => {
    mostrarMensaje('Negocio aprobado exitosamente', 'success');
    await cargarEmpresas();
    handleCerrarDialogAprobar();
  };

  /**
   * Abrir dialog de configuración
   */
  const handleAbrirDialogConfigurar = (empresa) => {
    setEmpresaSeleccionada(empresa);
    setDialogConfigurarOpen(true);
  };

  /**
   * Cerrar dialog de configuración
   */
  const handleCerrarDialogConfigurar = () => {
    setDialogConfigurarOpen(false);
    setEmpresaSeleccionada(null);
  };

  /**
   * Callback cuando se guarda configuración
   */
  const handleConfiguracionGuardada = async () => {
    mostrarMensaje('Configuración guardada correctamente', 'success');
    await cargarEmpresas();
  };

  /**
   * Navegar a crear empresa
   */
  const handleCrearEmpresa = () => {
    navigate('/admin/empresas/crear');
  };

  /**
   * Navegar a editar empresa
   */
  const handleEditarEmpresa = (id) => {
    navigate(`/admin/empresas/editar/${id}`);
  };

  /**
   * Mostrar mensaje en snackbar
   */
  const mostrarMensaje = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Cerrar snackbar
   */
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /**
   * Columnas para DataGrid (Tab "Todas")
   */
  const columnas = [
    {
      field: 'nombre',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'ruc',
      headerName: 'RUC',
      width: 150
    },
    {
      field: 'nombre_pais',
      headerName: 'País',
      width: 120
    },
    {
      field: 'correo_contacto',
      headerName: 'Correo',
      width: 200
    },
    {
      field: 'estado_proceso',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => {
        const estado = params.value || 'pendiente';
        const isPendiente = estado === 'pendiente';
        return (
          <Badge
            color={isPendiente ? 'warning' : 'success'}
            badgeContent={isPendiente ? 'Pendiente' : 'Activa'}
            sx={{
              '& .MuiBadge-badge': {
                position: 'relative',
                transform: 'none',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                px: 1.5,
                py: 0.5,
                borderRadius: 1
              }
            }}
          />
        );
      }
    },
    {
      field: 'nombre_plantilla',
      headerName: 'Plantilla',
      width: 150,
      renderCell: (params) => params.value || <em style={{ color: '#999' }}>Sin plantilla</em>
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const isPendiente = !params.row.estado_proceso || params.row.estado_proceso === 'pendiente';
        return (
          <Box display="flex" gap={1}>
            {isPendiente ? (
              <IconButton
                size="small"
                color="success"
                onClick={() => handleAbrirDialogAprobar(params.row)}
                title="Aprobar Negocio"
                sx={{
                  bgcolor: 'success.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'success.dark' }
                }}
              >
                <CheckCircleIcon />
              </IconButton>
            ) : (
              <>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleAbrirDialogConfigurar(params.row)}
                  title="Configurar Empresa"
                >
                  <TuneIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => handleEditarEmpresa(params.row.id)}
                  title="Editar"
                >
                  <EditIcon />
                </IconButton>
              </>
            )}
          </Box>
        );
      }
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 36 }} />
            Gestión de Empresas
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={0.5}>
            Administra negocios pendientes, empresas activas y configuración V1
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={cargarEmpresas}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCrearEmpresa}
            sx={{
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              fontWeight: 'bold'
            }}
          >
            Nueva Empresa
          </Button>
        </Box>
      </Box>

      {/* Paper principal */}
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        {/* Tabs */}
        <Tabs
          value={tabActual}
          onChange={handleChangeTab}
          sx={{
            bgcolor: '#F5F5F5',
            borderBottom: '2px solid #E0E0E0',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: '1rem',
              minHeight: 64,
              '&.Mui-selected': {
                color: '#1A237E'
              }
            }
          }}
        >
          <Tab
            label={
              <Badge badgeContent={pendientes.length} color="warning">
                Pendientes
              </Badge>
            }
            icon={<HourglassIcon />}
            iconPosition="start"
          />
          <Tab
            label={
              <Badge badgeContent={activas.length} color="success">
                Activas
              </Badge>
            }
            icon={<CheckCircleIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Todas (${empresas.length})`}
            icon={<BusinessIcon />}
            iconPosition="start"
          />
        </Tabs>

        {/* Loading */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Tab 1: Pendientes */}
            <TabPanel value={tabActual} index={0}>
              <Box px={3}>
                {pendientes.length === 0 ? (
                  <Alert severity="info">
                    No hay negocios pendientes de aprobación
                  </Alert>
                ) : (
                  <Grid container spacing={3}>
                    {pendientes.map(negocio => (
                      <Grid item xs={12} sm={6} md={4} key={negocio.id}>
                        <TarjetaEmpresa
                          empresa={negocio}
                          tipo="pendiente"
                          onAprobar={handleAbrirDialogAprobar}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </TabPanel>

            {/* Tab 2: Activas */}
            <TabPanel value={tabActual} index={1}>
              <Box px={3}>
                {activas.length === 0 ? (
                  <Alert severity="info">
                    No hay empresas activas
                  </Alert>
                ) : (
                  <Grid container spacing={3}>
                    {activas.map(empresa => (
                      <Grid item xs={12} sm={6} md={4} key={empresa.id}>
                        <TarjetaEmpresa
                          empresa={empresa}
                          tipo="activa"
                          onConfigurar={handleAbrirDialogConfigurar}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </TabPanel>

            {/* Tab 3: Todas */}
            <TabPanel value={tabActual} index={2}>
              <Box px={3}>
                <DataGrid
                  rows={empresas}
                  columns={columnas}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  pagination
                  disableSelectionOnClick
                  autoHeight
                  localeText={{
                    noRowsLabel: 'No hay empresas registradas',
                    footerRowSelected: (count) => `${count} fila(s) seleccionada(s)`,
                    MuiTablePagination: {
                      labelRowsPerPage: 'Filas por página:',
                      labelDisplayedRows: ({ from, to, count }) =>
                        `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
                    }
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #f0f0f0'
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#F5F5F5',
                      borderBottom: '2px solid #1A237E',
                      fontWeight: 'bold'
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'rgba(26, 35, 126, 0.04)'
                    }
                  }}
                />
              </Box>
            </TabPanel>
          </>
        )}
      </Paper>

      {/* Dialogs */}
      <DialogAprobarNegocio
        open={dialogAprobarOpen}
        negocio={negocioSeleccionado}
        onClose={handleCerrarDialogAprobar}
        onAprobado={handleNegocioAprobado}
      />

      <DialogConfigurarEmpresa
        open={dialogConfigurarOpen}
        empresa={empresaSeleccionada}
        onClose={handleCerrarDialogConfigurar}
        onConfiguracionGuardada={handleConfiguracionGuardada}
      />

      {/* Snackbar para mensajes */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GestionEmpresasUnificada;
